/** The deck editor's HTTP adapter over the generic item routes, via the kit's `request`. */

import { request } from '@gsf/kit';
import type { ApplesCardKind } from '@gsf/games';

/** A pack, as `GET /api/packs?gameId=` returns one. */
export interface DeckPack {
  id: string;
  gameId: string;
  title: string;
  description: string;
}

/** One card, as an item. `kind` is the colour and `body.text` is the word. */
export interface DeckCard {
  id: string;
  kind: ApplesCardKind;
  text: string;
  position: number;
}

interface ItemRow {
  id: string;
  kind: string;
  body: { text?: unknown };
  position: number;
}

/** Every deck this server has for this game. The first is the one a session plays. */
export async function listDecks(gameId: string): Promise<DeckPack[]> {
  return request<DeckPack[]>(`/packs?gameId=${encodeURIComponent(gameId)}`);
}

export async function listCards(packId: string): Promise<DeckCard[]> {
  const items = await request<ItemRow[]>(`/packs/${encodeURIComponent(packId)}/items`);
  return items
    .filter((item) => item.kind === 'green' || item.kind === 'red')
    .map((item) => ({
      id: item.id,
      kind: item.kind as ApplesCardKind,
      text: typeof item.body.text === 'string' ? item.body.text : '',
      position: item.position,
    }));
}

/**
 * Add a card. The server validates it against the game's schema, so a 422 carries the
 * game's own complaint; no client-side length check, as `MAX_CARD_TEXT` is on the schema.
 */
export async function addCard(
  packId: string,
  kind: ApplesCardKind,
  text: string,
): Promise<DeckCard> {
  const created = await request<ItemRow>(`/packs/${encodeURIComponent(packId)}/items`, {
    method: 'POST',
    body: JSON.stringify({ kind, body: { text } }),
  });
  return { id: created.id, kind, text, position: created.position };
}

/** Remove cards. Plural because `DELETE /api/items?ids=` is (see `routes/api.ts`). */
export async function removeCards(ids: readonly string[]): Promise<void> {
  if (ids.length === 0) return;
  await request<void>(`/items?ids=${ids.map(encodeURIComponent).join(',')}`, {
    method: 'DELETE',
  });
}
